import React, { useEffect, useState } from 'react';

import { createStudy } from '../../../../services/StudyService';
import { formatDateAsString } from '../../../../util/StringUtil';

import { hasRoles, roleDisplayValueMap, CREATE_STUDY_ROLES } from '../../../../util/RoleUtil';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Table } from '../../../common/table/Table';
import { StaffAssignments } from './StaffAssignments';
import Modal from '../../../common/modal/Modal';
import { CancelConfirmationModal } from '../../../common/cancel-confirmation-modal/CancelConfirmationModal';

import './StudyTrialsStaffTab.css';

export const StudyTrialsStaffTab = ({
  context,
  setContext,
  navigateTo,
  activeTab,
  setActiveTab,
  setSpinnerText,
  showToaster,
}) => {
  const [sites, setSites] = useState([]);
  const [activeChildTab, setActiveChildTab] = useState('Site 1');
  const [tabSelections, setTabSelections] = useState({});
  const [studyName, setStudyName] = useState('');
  const [assignedStaff, setAssignedStaff] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const [tabAssignedStaff, setTabAssignedStaff] = useState({});

  useEffect(() => {
    setStudyName(
      (context?.breadcrumbs?.newStudyGeneralBreadcrumb?.data?.protocolID || '') +
        ' ' +
        (context?.breadcrumbs?.newStudyGeneralBreadcrumb?.data?.studyName || '')
    );

    setActiveTab(activeTab);

    const sitesBreadcrumb = context.breadcrumbs.newStudySitesBreadcrumb?.data;
    if (sitesBreadcrumb && sitesBreadcrumb.length !== 0) {
      const selections = {};
      const assignedStaffByTab = {};

      const updatedSites = sitesBreadcrumb.map((site) => {
        const { name, facility, staff } = site;

        const siteSelections = {
          selectedFacility: { value: facility, label: facility.facility },
        };

        selections[name] = siteSelections;
        assignedStaffByTab[name] = staff;

        return { ...site, selections: siteSelections };
      });

      setSites(updatedSites);
      setTabSelections(selections);
      setTabAssignedStaff(assignedStaffByTab);
    }
  }, [activeTab, context, setActiveTab]);

  const closeModal = () => setIsModalOpen(false);
  const closeCancelModal = () => setIsCancelModalOpen(false);

  const getTabAssignedStaff = (tabName) => tabAssignedStaff[tabName] || [];
  const getTabSelections = (tabName) => tabSelections[tabName] || {};

  const handleAddStaffClick = () => {
    const activeTabAssignedStaff = getTabAssignedStaff(activeChildTab);
    setAssignedStaff(activeTabAssignedStaff);
    setIsModalOpen(true);
  };

  const handleSaveChangesClick = async () => {
    const updatedSites = sites.map((site) => {
      const selectionsForTab = getTabSelections(site.name);
      const selectedFacility = selectionsForTab?.selectedFacility;

      return {
        ...site,
        studyName: selectionsForTab?.studyName,
        selectedFacility: selectedFacility,
        staff: tabAssignedStaff[site.name] || [],
      };
    });

    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudySitesBreadcrumb: {
          ...prevState.breadcrumbs.newStudySitesBreadcrumb,
          type: 'General',
          screenKey: 'studyTrialStaff',
          data: updatedSites,
        },
      },
      editMode: true,
    }));

    const general = context.breadcrumbs.newStudyGeneralBreadcrumb?.data;
    if (!general) {
      showToaster({ responseStatus: 'Warning', responseMessage: 'General study details are missing.' });
      return;
    }

    const createStudySiteVOsMapped = updatedSites.map((site) => {
      const roles = {};
      (site.staff || []).forEach((member) => {
        if (!roles[member.studyRole]) roles[member.studyRole] = [];
        roles[member.studyRole].push(member.personExternalReference);
      });

      const rolesArray = Object.entries(roles).map(([roleName, personExternalReferences]) => ({
        roleName,
        personExternalReferences,
      }));

      return {
        accrualPeriodFrom: formatDateAsString(site.accrualFrom) || '',
        accrualPeriodTo: formatDateAsString(site.accrualTo) || '',
        endDate: formatDateAsString(site.endDate) || '',
        facilityExternalReference: site.facilityExternalReference || site.facility?.externalReference || '',
        firstEnrolmentDate: formatDateAsString(site.firstEnrollment) || '',
        roles: rolesArray,
        startDate: formatDateAsString(site.startDate) || '',
      };
    });

    const schedulesBreadcrumb = context.breadcrumbs.newStudySchedulesBreadcrumb?.data || {};
    const schedules = schedulesBreadcrumb.schedules || [];
    const scheduleData = schedulesBreadcrumb.scheduleData || {};

    const createStudyStudyScheduleVOS = (schedules || [])
      .map((s) => {
        const sched = scheduleData?.[s.id];
        if (!sched) return null;

        const scheduleName = (s.name || sched.scheduleName || '').trim();
        const scheduleVisits = Array.isArray(sched.visits) ? sched.visits : [];

        return {
          name: scheduleName,
          createStudyVisitScheduleVOS: scheduleVisits.map((v) => ({
            day: Number(v.day) || 0,
            formExternalReferences: Array.isArray(v.formExternalReferences)
              ? v.formExternalReferences
              : Array.isArray(v.forms)
              ? v.forms.map((f) => f.formExternalReference)
              : [],
            longVisit: !!v.longVisit,
            visitCode: v.visitCode || '',
            visitType: v.visitType || '',
            windowAfter: Number(v.windowAfter) || 0,
            windowBefore: Number(v.windowBefore) || 0,
            enrolmentVisit: !!v.enrolmentVisit,
          })),
        };
      })
      .filter(Boolean);

    const studyVO = {
      alertThreshHoldForDataCapture: general.alertThresholdForDataCapture || 3,
      alertThreshHoldForSDVCapture: general.alertThreshHoldForSDVCapture || 3,
      coEnrolmentAllowed: general.coEnrolmentAllowed || false,
      createStudySiteVOS: createStudySiteVOsMapped,
      createStudyStudyScheduleVOS,
      description: general.description || '',
      protocolID: general.protocolID || '',
      studyDurationPerParticipant: general.studyDurationPerParticipant || 0,
      studyDurationPerParticipantMetric: general.studyDurationPerParticipantMetric || '',
      studyName: general.studyName || '',
      type: general.type || '',
    };

    try {
      setSpinnerText('Creating Study');
      const [data, error] = await createStudy(context.username, studyVO);

      if (data?.responseStatus === 'Success') {
        // Clear all breadcrumbs
        setContext((prev) => ({
          ...prev,
          breadcrumbs: {
            newStudyGeneralBreadcrumb: undefined,
            newStudyFormsBreadcrumb: undefined,
            newStudySitesBreadcrumb: undefined,
            newStudySchedulesBreadcrumb: undefined,
          },
        }));

        await navigateTo('studyTrialAdmin');

        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Study Created',
        });
      } else if (data) {
        showToaster(data);
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error?.message || 'Unknown error',
        });
      }
    } finally {
      setSpinnerText(null);
    }
  };

  const handleCancelClick = () => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudySitesBreadcrumb: undefined,
        newStudyGeneralBreadcrumb: undefined,
        newStudyFormsBreadcrumb: undefined,
        newStudySchedulesBreadcrumb: undefined,
      },
      editMode: true,
    }));
    navigateTo('studyTrialAdmin');
  };

  const mapStaffMembers = () => {
    const staffMembers = tabAssignedStaff[activeChildTab];
    if (!staffMembers) return [];
    return staffMembers.map((staffMember) => ({
      ...staffMember,
      studyRole: roleDisplayValueMap.get(staffMember.studyRole) ?? staffMember.studyRole,
    }));
  };

  return (
    <div className="study-trials-staff-tab-container">
      <div className="study-trials-staff-tab-container-content">
        <div className="study-trials-staff-tab-tab-container">
          {sites.map((site) => (
            <div
              key={site.name}
              className={`study-trials-staff-tab ${activeChildTab === site.name ? 'active-tab' : ''}`}
              onClick={() => setActiveChildTab(site.name)}
            >
              {site.name}
            </div>
          ))}
        </div>

        <div className={`study-trials-staff-tab-content ${activeTab}`}>
          <div className="staff-tab-container" style={{ justifyContent: 'normal' }}>
            <h3>{studyName + ' ' + (getTabSelections(activeChildTab)?.selectedFacility?.label || '')}</h3>

            <Table
              tableData={mapStaffMembers()}
              hiddenColumns={['personExternalReference', 'systemUserExternalReference', 'selected', 'name']}
              searchEnabled={true}
              filterEnabled={true}
              onRowClick={null}
              tableTitle={'Staff'}
              buttonText={hasRoles(context, CREATE_STUDY_ROLES) ? 'Add/Edit' : undefined}
              onButtonClick={handleAddStaffClick}
              buttonOneWidth={'135px'}
              buttonDisabled={false}
              maxRowCount={5}
              horizontalScroll
            />
          </div>

          {isModalOpen && (
            <Modal
              closeModal={closeModal}
              setIsModalOpen={setIsModalOpen}
              headerTitle={studyName + ' ' + getTabSelections(activeChildTab)?.selectedFacility?.label}
              width={'950px'}
              height={'max-content'}
              contentPadding={'10px 4px'}
              justifyContent={'center'}
              hideButton={true}
            >
              <StaffAssignments
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                closeModal={closeModal}
                assignedStaff={assignedStaff}
                setAssignedStaff={setAssignedStaff}
                facilityExternalReference={getTabSelections(activeChildTab)?.selectedFacility?.value?.externalReference}
                setTabAssignedStaff={setTabAssignedStaff}
                activeChildTab={activeChildTab}
              />
            </Modal>
          )}

          {isCancelModalOpen && (
            <CancelConfirmationModal
              closeCancelModal={closeCancelModal}
              handleCancelClick={handleCancelClick}
              setIsCancelModalOpen={setIsCancelModalOpen}
            />
          )}
        </div>
      </div>

      <div className="study-button-container">
        <SaveButton
          color={'#ffffff'}
          border={'1px solid #74a47e'}
          textColor={'#74a47e'}
          title={'Cancel'}
          onClick={() => setIsCancelModalOpen(true)}
        />

        <SaveButton color={'#74a47e'} title={'Save Changes'} onClick={handleSaveChangesClick} />
      </div>
    </div>
  );
};
